import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Box, Button, Card, Chip, Stack, Typography } from "@mui/joy";
import { Link } from "react-router-dom";
import { apiClient } from "../../lib/api";

export interface JobWorkspaceData {
  photo_pairs: { before: number; after: number }[];
  document_families: { id: string; versions: number[]; current: number }[];
  measurement_selection?: {
    kind: "measurement" | "detailed";
    id: number;
  } | null;
  tasks: { open: number; overdue: number };
  next_task: { id: number; name: string; due_date: string } | null;
  financials: Record<string, string | number>;
  report_balance: string;
  evidence: {
    photos: number;
    documents: number;
    coc_photos: number;
    supplement_photos: number;
  };
}
export function useJobWorkspace(jobId: number | string) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["job-workspace", String(jobId)],
    enabled: !!jobId,
    queryFn: async () =>
      (await apiClient.get<JobWorkspaceData>(`/api/jobs/${jobId}/workspace/`))
        .data,
  });
  const command = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (
        await apiClient.post<JobWorkspaceData>(
          `/api/jobs/${jobId}/workspace/`,
          data,
        )
      ).data,
    onSuccess: (data) => {
      client.setQueryData(["job-workspace", String(jobId)], data);
      client.invalidateQueries({ queryKey: ["job-activity", String(jobId)] });
    },
  });
  return { ...query, command };
}
export function PageControls({
  page,
  count,
  hasNext,
  onChange,
  label = "records",
}: {
  page: number;
  count: number;
  hasNext: boolean;
  onChange: (page: number) => void;
  label?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        my: 1,
      }}
    >
      <Typography level="body-sm">
        {count} {label} · Page {page}
      </Typography>
      <Button
        size="sm"
        variant="outlined"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        Previous
      </Button>
      <Button
        size="sm"
        variant="outlined"
        disabled={!hasNext}
        onClick={() => onChange(page + 1)}
      >
        Next
      </Button>
    </Box>
  );
}
export function useCollectionPage<T>(items: T[], label: string, size = 20) {
  const [requested, setPage] = useState(1);
  const page = Math.min(requested, Math.max(1, Math.ceil(items.length / size)));
  return {
    rows: items.slice((page - 1) * size, page * size),
    controls: (
      <PageControls
        page={page}
        count={items.length}
        hasNext={page * size < items.length}
        onChange={setPage}
        label={label}
      />
    ),
  };
}
export function JobActivityPanel({ jobId }: { jobId: number | string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["job-activity", String(jobId), page],
    queryFn: async () =>
      (
        await apiClient.get<{
          count: number;
          next: string | null;
          results: {
            id: number;
            actor: string;
            description: string;
            created_at: string;
          }[];
        }>(`/api/jobs/${jobId}/activity/?page=${page}`)
      ).data,
  });
  return (
    <Card sx={{ mb: 3 }}>
      <Typography level="title-lg">Job activity</Typography>
      {error ? (
        <Alert
          color="danger"
          endDecorator={<Button onClick={() => refetch()}>Retry</Button>}
        >
          Could not load activity.
        </Alert>
      ) : isLoading ? (
        <Typography>Loading activity…</Typography>
      ) : (
        <>
          <Stack spacing={1}>
            {data?.results.map((entry) => (
              <Box
                key={entry.id}
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  pb: 1,
                }}
              >
                <Typography level="title-sm">{entry.description}</Typography>
                <Typography level="body-xs">
                  {entry.actor || "System"} ·{" "}
                  {new Date(entry.created_at).toLocaleString()}
                </Typography>
              </Box>
            ))}
            {!data?.count && (
              <Typography level="body-sm">
                New changes to this job will appear here.
              </Typography>
            )}
          </Stack>
          <PageControls
            page={page}
            count={data?.count || 0}
            hasNext={!!data?.next}
            onChange={setPage}
            label="events"
          />
        </>
      )}
    </Card>
  );
}
export function JobNextActions({ job }: { job: any }) {
  const { data, error } = useJobWorkspace(job.id);
  return (
    <Card sx={{ mb: 3 }}>
      <Typography level="title-lg">Next actions</Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Chip color={job.build_date ? "primary" : "warning"}>
          Build: {job.build_date || "Not scheduled"}
        </Chip>
        <Chip color={job.assigned_to ? "success" : "warning"}>
          {job.assigned_to ? "Owner assigned" : "Assign a job owner"}
        </Chip>
        <Chip color={(job.associated_contact || job.contacts?.length) ? "success" : "warning"}>
          {(job.associated_contact || job.contacts?.length) ? "Contact linked" : "Link a contact"}
        </Chip>
      </Box>
      {error ? (
        <Alert color="warning">Task readiness could not be loaded.</Alert>
      ) : (
        <>
          <Typography>
            {data?.tasks.open ?? "…"} open tasks · {data?.tasks.overdue ?? "…"}{" "}
            overdue
          </Typography>
          {data?.next_task && (
            <Button
              component={Link}
              to={`/operations/tasks?task=${data.next_task.id}`}
              variant="outlined"
            >
              Next: {data.next_task.name}
            </Button>
          )}
        </>
      )}
    </Card>
  );
}
const money = (value: unknown) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
export function JobFinancialOverview({
  jobId,
  onTab,
}: {
  jobId: number | string;
  onTab: (index: number) => void;
}) {
  const { data, isLoading, error, refetch } = useJobWorkspace(jobId);
  if (error)
    return (
      <Alert
        color="danger"
        endDecorator={<Button onClick={() => refetch()}>Retry</Button>}
      >
        Could not load financial totals.
      </Alert>
    );
  if (isLoading || !data)
    return <Typography>Loading financial totals…</Typography>;
  const f = data.financials;
  return (
    <Card sx={{ mb: 2 }}>
      <Typography level="title-lg">Job financial overview</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,1fr)",
            lg: "repeat(3,1fr)",
          },
          gap: 1,
        }}
      >
        {[
          ["Contract value", f.job_value, 5],
          ["Payments received", f.total_payments, 5],
          ["Uncollected contract value", f.outstanding, 5],
          ["Issued invoice balance", f.issued_receivables, 5],
          ["Actual costs, net of credits", f.total_costs, 7],
          ["Credits", f.total_credits, 7],
          ["Open material commitments", f.committed_material_orders, 8],
        ].map(([label, value, tab]) => (
          <Button
            key={String(label)}
            variant="soft"
            onClick={() => onTab(Number(tab))}
            sx={{ display: "block", textAlign: "left", whiteSpace: "normal" }}
          >
            {String(label)}
            <Typography level="h4">{money(value)}</Typography>
          </Button>
        ))}
      </Box>
      <Typography level="body-sm">
        Material commitments exclude amounts matched to recorded costs in the Profit Desk.
        Uncollected contract value may include work that has not been invoiced.
      </Typography>
    </Card>
  );
}
export function WorkOrderReadiness({
  jobId,
  order,
}: {
  jobId?: number;
  order: any;
}) {
  const { data, error } = useJobWorkspace(jobId || order.job || 0);
  const checks = [
    ["Scope", !!(order.scope_of_work || order.description)],
    ["Crew", !!order.subcontractor],
    ["Dates", !!order.start_date && !!order.end_date],
    ["Job documents", !!data?.evidence.documents],
    ["No overdue dependencies", data?.tasks.overdue === 0],
  ] as const;
  return (
    <Card variant="soft" sx={{ my: 2 }}>
      <Typography level="title-md">Work-order basics</Typography>
      <Button component={Link} variant="outlined" size="sm" to={`/operations/scheduling?workspace=production&job=${jobId || order.job}`}>
        Review production prerequisites
      </Button>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {checks.map(([label, ready]) => (
          <Chip key={label} color={ready ? "success" : "warning"}>
            {label}: {ready ? "Ready" : "Review"}
          </Chip>
        ))}
      </Box>
      <Typography level="body-sm">
        {order.start_date || "Start not set"} →{" "}
        {order.end_date || "End not set"} · {data?.tasks.open ?? "…"} open job
        tasks
      </Typography>
      <Typography level="body-xs">
        Last status change:{" "}
        {order.status_changed_at
          ? new Date(order.status_changed_at).toLocaleString()
          : "Not recorded for this work order"}
      </Typography>
      {error && (
        <Alert color="warning">
          Job readiness is unavailable. Review the job before sending.
        </Alert>
      )}
      <Typography level="body-xs">
        Confirm which job documents and outstanding tasks are required for this
        crew. These checks do not change the work-order lifecycle.
      </Typography>
    </Card>
  );
}
export function CloseoutChecklist({
  job,
  kind,
  recipient,
}: {
  job: any;
  kind: "coc" | "supplement";
  recipient?: string;
}) {
  const { data, error, refetch } = useJobWorkspace(job.id);
  const items = [
    ["Recipient", !!recipient?.trim(), "overview"],
    ["Claim number", !!job.claim_number, "overview"],
    ["Carrier", !!job.insurance_company, "overview"],
    [
      "Tagged report photos",
      !!(kind === "coc"
        ? data?.evidence.coc_photos
        : data?.evidence.supplement_photos),
      "photos",
    ],
    ["Documents", !!data?.evidence.documents, "documents"],
    ["Tasks complete", data?.tasks.open === 0, "tasks"],
  ] as const;
  return (
    <Card variant="soft" sx={{ mb: 2 }}>
      <Typography level="title-lg">Closeout readiness</Typography>
      {error ? (
        <Alert
          color="danger"
          endDecorator={<Button onClick={() => refetch()}>Retry</Button>}
        >
          Financial and evidence checks are unavailable.
        </Alert>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {items.map(([name, ready, tab]) => (
              <Button
                key={name}
                size="sm"
                variant="outlined"
                color={ready ? "success" : "warning"}
                component="a"
                href={`/operations/jobs/${job.id}?tab=${tab}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {name}: {ready ? "Ready" : "Review"}
              </Button>
            ))}
          </Box>
          <Typography level="body-sm">
            Payments received: {money(data?.financials.total_payments)} ·
            Remaining contract balance: {money(data?.report_balance)}
          </Typography>
        </>
      )}
      <Typography level="body-xs">
        Review the recipient, selected attachments and report amounts below
        before generating or sending. Readiness warnings are advisory.
      </Typography>
    </Card>
  );
}
